import _ from 'lodash';
import moment from 'moment';
import phoneNumber from '@/utils/phoneNumber';
import Segment from '../../Segment';
import Intercom from '../../Intercom';
import DDLogs from '../../DDLogs';
import CustomerApi from '../../api/CustomersApi';
import CustomerActivityApi from '../../api/CustomerActivityApi';
import HansApi from '../../api/HansApi';
import SubscriptionStateApi from '../../api/SubscriptionStateApi';
import router from '../../router/index';
import featureFlag from '../../api/featureFlag';
import InvalidCredentialsStateApi from '../../api/InvalidCredentialsStateApi';

function _getDaysSince(subscriptionState) {
  return moment().diff(moment(subscriptionState.since), 'days');
}

const state = {
  authenticated: false,
  inviteToken: '',
  customerId: null,
  customerDetails: {},
  members: [],
};

const getters = {
  firstName: state => state.customerDetails.firstName,
  lastName: state => state.customerDetails.lastName,
  secondMemberName: state => state.customerDetails.secondMemberName,
  numberOfMembers: state => state.members.length,
  householdName: state => state.customerDetails.householdName,
  primaryMember: state => {
    const primaryMember = state.customerDetails.primaryMember || {};
    return {
      firstName: primaryMember.firstName,
      lastName: primaryMember.lastName,
      phoneNumber: phoneNumber.formatPhoneNumber(primaryMember.phoneNumber),
    };
  },
};

const actions = {
  async init({ commit, getters }, { incognito, name }) {
    try {
      const customerDetails = await CustomerApi.getCustomerDetails();
      const onboardingStatus = await CustomerApi.getOnboradingStatus();
      const members = await CustomerApi.getMembers();
      const inviteToken = await HansApi.generateInviteToken();
      const subscriptionState = await SubscriptionStateApi.fetchSubscriptionState();
      const invalidCredentialsState = await InvalidCredentialsStateApi.fetchInvalidCredentialsState();
      // eslint-disable-next-line max-len
      const cxTrialMessagesTest = (await featureFlag.getTreatment('cx-trial-messages', { subscriptionState: subscriptionState.subscriptionState })) === 'on';
      commit('setCustomerId', customerDetails.customerId);
      DDLogs.addGlobalContext('customerId', customerDetails.customerId);
      commit('setAuthenticated', true);
      commit('setCustomerDetails', customerDetails);
      commit('setMembers', members);
      commit('setInviteToken', inviteToken.token);
      Segment.setIncognito(incognito);
      Segment.identifyCustomer(customerDetails.customerId);
      if (!incognito) {
        const isOnboarding = onboardingStatus !== 'completed';
        const daysInTrial = subscriptionState.state === 'trial' ? _getDaysSince(subscriptionState) : undefined;
        const daysInInvalidCredentials = invalidCredentialsState ? Math.abs(moment(invalidCredentialsState.since).diff(moment(), 'days')) : 0;
        const primaryMember = _.find(members, { primary: true });
        const email = primaryMember ? primaryMember.email : null;
        await Intercom.boot(
          customerDetails.customerId, getters.householdName,
          {
            userHash: customerDetails.intercomUserHash,
            numberOfMembers: getters.numberOfMembers,
            isOnboarding,
            daysInTrial,
            cxTrialMessagesTest,
            daysInInvalidCredentials,
            email,
          },
        );
        await CustomerActivityApi.setCustomerActivity({
          activityTime: moment(),
          details: {
            activityType: 'visitPage',
            pageName: name,
          },
        });
      }

      const { showIntercom } = router.currentRoute.query;
      if (showIntercom) {
        Intercom.show();
      }
    } catch (e) {
      // Added this in order to avoid sentry issues ith 403 errors
      console.log('Could not authenticate', e);
    }
  },
  setAuthenticated({ commit }, value) {
    commit('setAuthenticated', value);
  },
};

const mutations = {
  setAuthenticated(state, authenticateRespond) {
    state.authenticated = authenticateRespond;
  },
  setCustomerId(state, customerId) {
    state.customerId = customerId;
  },
  setCustomerDetails(state, customerDetails) {
    state.customerDetails = customerDetails;
  },
  setMembers(state, members) {
    state.members = members;
  },
  setInviteToken(state, inviteToken) {
    state.inviteToken = inviteToken;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
